import { Fragment } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    type ColumnDef,
    flexRender,
} from "@tanstack/react-table";
import "./style.css";

interface TableProps<TData> {
    data: TData[];
    headers: Array<ColumnDef<TData>>;
}

const DefaultTable = <TData extends object>(props: TableProps<TData>) => {
    const { data, headers } = props;

    const columns = [...headers];

    const table = useReactTable<TData>({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const handlePagination = (navigation: string) => {
        navigation === "prev" ? table.previousPage() : table.nextPage();
        table.setState((prevState) => {
            return {
                ...prevState,
            };
        });
    };

    const setPageSize = (pageSize: number) => {
        table.setPageIndex(0);
        table.setPageSize(pageSize);
    };

    return (
        <div className="table__wrapper default__table">
            <div className="table-responsive">
                <table className="table table-striped table-bordered mb-0">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef.header,
                                                      header.getContext()
                                                  )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Fragment key={row.id}>
                                    <tr>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="cmp-table__pagination border p-1 rounded mt-4">
                <div className="row align-items-center">
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                handlePagination("prev");
                            }}
                            disabled={!table.getCanPreviousPage()}
                        >
                            {"<"} Previous
                        </button>
                    </div>
                    <div className="col-8 d-flex align-items-center justify-content-between">
                        <div className="cmp-table__pages">
                            Page{" "}
                            <strong>
                                {table.getPageCount() && table.getState().pagination.pageIndex + 1}{" "}
                                of {table.getPageCount()}
                            </strong>
                        </div>

                        <div className="d-inline-flex align-items-center">
                            Show
                            <select
                                className="form-control mb-0 h-auto p-1 ml-2"
                                style={{ minHeight: "40px" }}
                                value={table.getState().pagination.pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                handlePagination("next");
                            }}
                            disabled={!table.getCanNextPage()}
                        >
                            Next {">"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DefaultTable;
